import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {number} */
  #startX = 0;

  /** @type {number} */
  #currentX = 0;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {AbortController | null} */
  #swipeController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#syncSlideVisibility();
    this.#setupSwipe();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    requestAnimationFrame(() => {
      this.#syncSlideVisibility();
      this.play();
    });
  }

  updatedCallback() {
    super.updatedCallback();

    if (this.#getSlides().length === 0) return;

    if (!this.#interval && this.autoplay) {
      this.#syncSlideVisibility();
      this.play();
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getSlides() {
    const refSlides = this.refs.slides ?? [];
    if (refSlides.length > 0) return refSlides;

    return [
      ...this.querySelectorAll(
        '.announcement-bar__slides > slideshow-slide, .announcement-bar__slides > .announcement-bar__slide'
      ),
    ];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#swipeController?.abort();
    this.#swipeController = null;
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in milliseconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.suspend();
    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.suspend();
    this.paused = false;
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.#getSlides();
    if (slides.length === 0) return;

    this.#current = current;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    this.#syncSlideVisibility(relativeIndex);
  }

  /**
   * Pause the slideshow when the page is hidden; resume autoplay when visible again.
   */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
      return;
    }

    if (!this.autoplay) return;

    this.paused = false;
    this.suspend();
    this.play();
  };

  /**
   * Updates aria-hidden on slides for visibility (CSS-driven fade).
   * @param {number} [activeIndex]
   */
  #syncSlideVisibility(activeIndex = this.#current) {
    const slides = this.#getSlides();
    if (slides.length === 0) return;

    let relativeIndex = activeIndex % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    this.#current = relativeIndex;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #setupSwipe() {
    const slides = this.#getSlides();
    if (slides.length <= 1) return;

    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (!(slidesContainer instanceof HTMLElement)) return;

    this.#swipeController?.abort();
    this.#swipeController = new AbortController();
    const { signal } = this.#swipeController;

    slidesContainer.addEventListener('touchstart', this.#handleTouchStart, { passive: true, signal });
    slidesContainer.addEventListener('touchmove', this.#handleTouchMove, { passive: true, signal });
    slidesContainer.addEventListener('touchend', this.#handleTouchEnd, { signal });
    slidesContainer.addEventListener('mousedown', this.#handleMouseDown, { signal });
    slidesContainer.addEventListener('mousemove', this.#handleMouseMove, { signal });
    slidesContainer.addEventListener('mouseup', this.#handleMouseUp, { signal });
    slidesContainer.addEventListener('mouseleave', this.#handleMouseUp, { signal });
  }

  #handleTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch) return;

    this.#startX = touch.clientX;
    this.#isDragging = true;
  };

  #handleTouchMove = (event) => {
    if (!this.#isDragging) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.#currentX = touch.clientX;
  };

  #handleTouchEnd = () => {
    if (!this.#isDragging) return;

    this.#isDragging = false;
    this.#handleGesture();
  };

  #handleMouseDown = (event) => {
    this.#startX = event.clientX;
    this.#isDragging = true;
  };

  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;

    this.#currentX = event.clientX;
  };

  #handleMouseUp = () => {
    if (!this.#isDragging) return;

    this.#isDragging = false;
    this.#handleGesture();
  };

  #handleGesture() {
    const diffX = this.#startX - this.#currentX;
    const swipeThreshold = 50;

    if (Math.abs(diffX) <= swipeThreshold) return;

    if (diffX > 0) {
      this.next();
    } else {
      this.previous();
    }
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
